import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Video, ArrowLeft, Wand2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import GenerateVideoModal from '../components/GenerateVideoModal';
import GenerateWithAIModal from '../components/GenerateWithAIModal';
import { useAuth } from '../contexts/AuthContext';
import RequireAuthModal from '../components/auth/RequireAuthModal';
import LoginModal from '../components/auth/LoginModal';

const GenerateWithAI: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showRequireAuth, setShowRequireAuth] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const buttonVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 15,
      },
    },
    hover: {
      scale: 1.05,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10,
      },
    },
    tap: {
      scale: 0.95,
    },
  };

  return (
    <div className="min-h-screen px-4 pt-20 pb-24">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-4xl mx-auto"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200 mb-6"
          >
            <ArrowLeft size={20} />
            <span>{t('back')}</span>
          </button>
          
          <h1 className="text-3xl md:text-4xl font-contrail font-bold text-gray-900 dark:text-gray-100">
            {t('generateWithAI')}
          </h1>
        </motion.div>

        {/* Main Buttons */}
        <motion.div
          variants={itemVariants}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8"
        >
          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => { if (!user) { setShowRequireAuth(true); return; } setShowAIModal(true); }}
            className="card p-8 text-left group hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                <Wand2 className="w-6 h-6 text-purple-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {t('generateModelWithAI')}
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300">
              {t('generateModelWithAIDescription')}
            </p>
          </motion.button>

          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => { if (!user) { setShowRequireAuth(true); return; } setShowVideoModal(true); }}
            className="card p-8 text-left group hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                <Video className="w-6 h-6 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {t('generateVideoWithAI')}
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300">
              {t('generateVideoWithAIDescription')}
            </p>
          </motion.button>
        </motion.div>

        {/* Modals */}
        <AnimatePresence>
          {showVideoModal && (
            <GenerateVideoModal
              isOpen={showVideoModal}
              onClose={() => setShowVideoModal(false)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showAIModal && (
            <GenerateWithAIModal
              isOpen={showAIModal}
              onClose={() => setShowAIModal(false)}
            />
          )}
        </AnimatePresence>

        <RequireAuthModal
          isOpen={showRequireAuth}
          onClose={() => setShowRequireAuth(false)}
          onLoginClick={() => { setShowRequireAuth(false); setShowLoginModal(true); }}
        />
        <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      </motion.div>
    </div>
  );
};

export default GenerateWithAI;
